// Login screen — the only route reachable while unauthenticated. On
// success it routes the user back to where the guard intercepted them
// (or /home). Already-authenticated visitors are bounced straight in.

import { useState } from 'react';
import { Button, Card, Form, Input, Typography, message } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/rest';

interface LoginValues {
  email: string;
  password: string;
}

interface FromState {
  from?: { pathname?: string };
}

export default function Login() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);

  const redirectTo =
    (location.state as FromState | null)?.from?.pathname ?? '/home';

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  const onFinish = async (values: LoginValues) => {
    setSubmitting(true);
    try {
      await login(values.email.trim(), values.password);
      message.success('登录成功');
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.status === 401
            ? '邮箱或密码错误'
            : err.message
          : err instanceof Error
            ? err.message
            : '登录失败';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fafafa',
      }}
    >
      <Card style={{ width: 360 }} bordered>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>
            carrier
          </Typography.Title>
          <Typography.Text type="secondary">登录以访问仪表盘</Typography.Text>
        </div>
        <Form<LoginValues>
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
          disabled={submitting}
        >
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '邮箱格式不正确' },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="user@example.com"
              autoComplete="username"
              autoFocus
            />
          </Form.Item>
          <Form.Item
            name="password"
            label="密码"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
              autoComplete="current-password"
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" block loading={submitting}>
              登录
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
